import React, { useState, memo, useMemo, useEffect, Fragment, useCallback } from 'react';
import { useTranslation } from "core/hooks/useTranslation";
import { useParams } from "react-router-dom";

import { Row, Col } from "core/ui-kit/layout";
import Form from "core/ui-kit/form";
import Modal from "core/ui-kit/modal";

import { POPUP_SIZE } from "core/constants/common";

import { FORM_FIELD_NAMES } from './constants';

import campaignService from 'core/services/apiServices/campaignService';

import showSuccess from 'core/helpers/common/showSuccess';
import FormRulesBuilder from 'core/helpers/common/formRulesBuilder';
import FailureModal from './failureModal';

const AddCampaignAffiliates = ({
    isVisible,
    onSave,
    availableAffiliates,
    onClose
}) => {
    const { t } = useTranslation();
    const [formInstance] = Form.useForm();

    const searchParams = useParams();

    const [failureModalData, setFailureModalData] = useState({
        isVisible: false,
        data: null
    });

    const [isDisabled, setIsDisabled] = useState(true);

    const [isSaving, setIsSaving] = useState(false);

    const campaignId = searchParams.id;

    const defaultValues = {
        [FORM_FIELD_NAMES.AFFILIATE_IDS] : []
    };

    const onOkButtonClick = () => {
        formInstance.submit()
    }

    const handleValuesChange = (_, formValues) => {
        setIsDisabled(formValues[FORM_FIELD_NAMES.AFFILIATE_IDS].length === 0)
    }

    const onSuccess = (value, message, affiliatesToAdd) => {

        const failedAffiliatesIds = value.failedAffiliates.map(affiliate => affiliate.id);
        
        const successAfffiliates = affiliatesToAdd.filter(affilite => !failedAffiliatesIds.includes(affilite.id))
        if(value.failureCount > 0){
            setFailureModalData({
                isVisible: true,
                data: value
            })
        } else {
            showSuccess(message);
        }
        onSave(successAfffiliates);
        onClose();
    }

    const handleFormFinish = formData => {
        setIsSaving(true);
        const d = {
            id: campaignId,
            affiliateIds: formData[FORM_FIELD_NAMES.AFFILIATE_IDS]
        }

        const affiliatesToAdd = availableAffiliates.filter(affiliate => d.affiliateIds.includes(affiliate.id) )

        campaignService.addAffiliates(d)
            .then(({ value, message }) => {
                onSuccess(value, message, affiliatesToAdd)
            })
            .finally(() => setIsSaving(false))
    };

    const affiliatesOptions = useMemo(() => {
        return availableAffiliates.map(c => ({ value: c.id, text: c.name }))
    }, [availableAffiliates])


    useEffect(() => {
        if(isVisible){
            formInstance.setFieldsValue(defaultValues);
            setIsDisabled(true);
        }
    }, [isVisible])

    const hideFailureModal = useCallback(() => {
        setFailureModalData({
            isVisible: false,
            data: null
        })
    }, [setFailureModalData])

    return (
        <Fragment>
            <Modal
                open={isVisible}
                title={ t('backoffice.campaigns.addAffiliates') }
                cancelText={t('backoffice.common.cancel')}
                okText={ t('backoffice.common.add')}
                onOk={onOkButtonClick}
                onCancel={onClose}
                size={POPUP_SIZE.SMALL}
                okButtonProps={{ disabled: isDisabled }}
                isLoading={isSaving}
            >
                <Form
                    form={formInstance}
                    onValuesChange={ handleValuesChange }
                    onFinish={handleFormFinish}
                >
                    <Row gutter={[16, 0]}>
                        <Col span={24}>
                            <Form.Select
                                label={ t('backoffice.campaigns.affiliate')}
                                placeholder={`${t("backoffice.common.select")} ${t('backoffice.campaigns.affiliate')}`}
                                name={FORM_FIELD_NAMES.AFFILIATE_IDS}
                                rules={
                                    new FormRulesBuilder(t).required({whitespace: false}).build()
                                }
                                options={ affiliatesOptions }
                                search={true}
                                isMultiple={true}
                                className="rt--form-item-without-margin"
                            />
                        </Col>
                    </Row>
                </Form>
            </Modal>

            <FailureModal 
                isVisible={failureModalData.isVisible}
                data={failureModalData.data}
                onClose={hideFailureModal}
            />
        </Fragment>
        
    )
}

export default memo(AddCampaignAffiliates);